import { Button, Divider } from '@otter-ai/den-components';
import { HubspotIcon } from '@otter-ai/den-icons';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { FormattedText } from '@/shared/components/FormattedText';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import { openConfirmDialog } from '@/shared/modals/confirm/openConfirmDialog';
import { BUILT_IN_APPS } from '@/shared/types/apps';
import { descriptionFormatRules } from '../../shared/utils/text';
import { useCrmPaywall } from '../hooks/useCrmPaywall';
import { useHubspotOAuth } from '../hooks/useHubspotOAuth';
import { useHubspotSettings } from '../hooks/useHubspotSettings';
import { useSyncedAccounts } from '../hooks/useSyncedAccounts';
import { HUBSPOT_TEST_IDS } from '../test-ids/hubspot';
import { trackCRM } from '../tracking/crm-tracking';
import type { HubspotSyncedAccount } from '../type/apps';
import { AppRowLayout } from './AppRowLayout';
import { CrmLimitPaywall } from './CrmLimitPaywall';
import { CrmUpgradeButton } from './CrmUpgradeButton';

export function HubspotAppRow() {
  const t = useTranslations('settings:integrations.apps.hubspot');
  const { adminStatus } = useWorkspace();
  const {
    canManageTeamHubspot,
    openHubspotTeamManageModal,
    canConnectHubspotAccount,
    canAddAllHubspotAccounts,
    disableDisconnectHubspot,
    isConnectToHubspot,
    canShowButtons,
  } = useHubspotSettings();
  const {
    openHubspotAuthorizationPage,
    disconnectHubspot,
    connectAllHubspotAccounts,
  } = useHubspotOAuth();

  const paywallInfo = useCrmPaywall('Hubspot');

  const onDisconnectClick = useCallback(async () => {
    trackCRM.main.clickDisconnect('hubspot');
    const confirmed = await openConfirmDialog({
      title: t('disconnectModal.title'),
      message: (
        <FormattedText>
          {t.rich('disconnectModal.description', descriptionFormatRules())}
        </FormattedText>
      ),
      confirmText: t('disconnectModal.confirmText'),
      cancelText: t('disconnectModal.cancelText'),
      danger: true,
    });
    if (!confirmed) {
      return;
    }

    try {
      await disconnectHubspot();
      trackCRM.main.disconnected('hubspot');
    } catch {
      trackCRM.main.disconnectedFailed('hubspot');
    }
  }, [disconnectHubspot]);

  const extendContent = (
    <HubspotExtendContent
      canAddAllHubspotAccounts={canAddAllHubspotAccounts ?? false}
      connectAllHubspotAccounts={connectAllHubspotAccounts}
    />
  );

  // Show paywall: always when not connected, or only to admins when connected
  const shouldShowPaywallBanner =
    paywallInfo.shouldShowPaywall &&
    (!isConnectToHubspot || adminStatus?.isAdmin);

  const paywallContent = shouldShowPaywallBanner && (
    <CrmLimitPaywall
      service="hubspot"
      messageKey={paywallInfo.paywallMessageKey}
      connectionsRemaining={paywallInfo.connectionsRemaining}
      showUpgradeButton={paywallInfo.showUpgradeButton}
      showContactSalesButton={paywallInfo.showContactSalesButton}
      connectionsUsage={paywallInfo.connectionsUsage}
      limitReached={paywallInfo.isCrmLimitReached}
    />
  );

  return (
    <AppRowLayout
      appRowId="hubspot-app"
      Icon={HubspotIcon}
      tags={{
        enterpriseTag: !paywallInfo.isCrmPaywallEnabled,
        proTag: paywallInfo.isCrmPaywallEnabled && paywallInfo.isFreePlan,
      }}
      title="HubSpot"
      description={<HubspotDescription />}
      buttons={
        canShowButtons && (
          <div className="flex gap-2 flex-col">
            {/* Disconnect button */}
            {isConnectToHubspot && (
              <Button
                variant="outlineNeutral"
                version="legacy"
                onClick={onDisconnectClick}
                disabled={disableDisconnectHubspot ?? false}
                data-testid={HUBSPOT_TEST_IDS.disconnectButton}
              >
                {t('disconnectButton')}
              </Button>
            )}
            {/* Upgrade button for free users in CRM experiment */}
            {!canConnectHubspotAccount &&
              paywallInfo.isCrmPaywallEnabled &&
              paywallInfo.isFreePlan && (
                <CrmUpgradeButton
                  service="hubspot"
                  limitReached={paywallInfo.isCrmLimitReached}
                  connectionsUsage={paywallInfo.connectionsUsage}
                />
              )}
            {/* Schedule demo button */}
            {!canConnectHubspotAccount && (
              <Button
                onClick={() => {
                  trackCRM.main.clickScheduleDemo();
                  window.open(t('scheduleUrl'), '_blank');
                }}
              >
                {t('scheduleDemoButton')}
              </Button>
            )}
            {/* Add button - disabled when CRM limit is reached */}
            {canConnectHubspotAccount && !isConnectToHubspot && (
              <Button
                onClick={() => {
                  trackCRM.main.clickConnect('hubspot');
                  openHubspotAuthorizationPage({ connectionType: 'member' });
                }}
                data-testid={HUBSPOT_TEST_IDS.connectButton}
                disabled={paywallInfo.isCrmLimitReached}
              >
                {t('addButton')}
              </Button>
            )}
            {/* Manage button for admin */}
            {canManageTeamHubspot && (
              <Button
                onClick={() => {
                  trackCRM.main.clickManageFields('hubspot');
                  openHubspotTeamManageModal();
                }}
                data-testid={HUBSPOT_TEST_IDS.manageButton}
              >
                {t('manageButton')}
              </Button>
            )}
          </div>
        )
      }
      extendContent={extendContent}
      fullWidthContent={paywallContent}
    />
  );
}

function HubspotDescription() {
  const { accounts, isLoading: isLoadingAccounts } = useSyncedAccounts({
    service: BUILT_IN_APPS.HUBSPOT,
  });
  const { isLoading: isLoadingWorkspace, adminStatus } = useWorkspace();
  const hubspotAccount = accounts?.[0] as HubspotSyncedAccount | undefined;
  const { canManageTeamHubspot } = useHubspotSettings();

  if (isLoadingAccounts || isLoadingWorkspace) {
    return null;
  }

  if (!hubspotAccount) {
    return <HubspotBeforeConnectDescription />;
  }

  return (
    <>
      <HubspotConnectedAccountDescription
        hubspotAccount={hubspotAccount as HubspotSyncedAccount}
      />
      <HubspotAdminManageDescription
        canManageTeamHubspot={canManageTeamHubspot ?? false}
      />
      <HubspotAdminConnectedAllDescription
        hubspotAccount={hubspotAccount as HubspotSyncedAccount}
        isAdmin={adminStatus?.isAdmin ?? false}
      />
      <HubspotIndividualUserConnectedDescription
        hubspotAccount={hubspotAccount as HubspotSyncedAccount}
        isAdmin={adminStatus?.isAdmin ?? false}
      />
    </>
  );
}

function HubspotExtendContent({
  canAddAllHubspotAccounts,
  connectAllHubspotAccounts,
}: {
  canAddAllHubspotAccounts: boolean;
  connectAllHubspotAccounts: () => void;
}) {
  const t = useTranslations('settings:integrations.apps.hubspot');
  if (!canAddAllHubspotAccounts) {
    return null;
  }

  return (
    <>
      <Divider orientation="horizontal" className="my-4" />
      <div className="flex flex-col gap-4 mr-[100px]">
        <HubspotAdminConnectAllDescription
          canAddAllHubspotAccounts={canAddAllHubspotAccounts}
        />
        <div>
          <Button
            onClick={() => {
              trackCRM.main.clickConnectAll('hubspot');
              connectAllHubspotAccounts();
            }}
          >
            {t('connectAllButton')}
          </Button>
        </div>
      </div>
    </>
  );
}

function HubspotBeforeConnectDescription() {
  const t = useTranslations('settings:integrations.apps.hubspot');
  return (
    <FormattedText>
      {t.rich(
        'descriptionBeforeConnect',
        descriptionFormatRules({
          links: {
            'installation-guide-link': {
              href: t('installationGuideUrl'),
              target: '_blank',
            },
          },
        }),
      )}
    </FormattedText>
  );
}

function HubspotConnectedAccountDescription({
  hubspotAccount,
}: {
  hubspotAccount: HubspotSyncedAccount;
}) {
  const t = useTranslations('settings:integrations.apps.hubspot');
  if (!hubspotAccount.creator_email) {
    return null;
  }

  return (
    <FormattedText>
      {t.rich('connectedHubspotAccount', {
        'email-link': (chunk) => (
          <a href={`mailto:${hubspotAccount.creator_email}`}>{chunk}</a>
        ),
        creatorEmail: hubspotAccount.creator_email,
      })}
    </FormattedText>
  );
}

function HubspotAdminConnectAllDescription({
  canAddAllHubspotAccounts,
}: {
  canAddAllHubspotAccounts: boolean;
}) {
  const t = useTranslations('settings:integrations.apps.hubspot');

  if (!canAddAllHubspotAccounts) {
    return null;
  }
  return (
    <div className="text-sm text-subtle">
      <FormattedText>
        {t.rich(
          'descriptionAdminConnectAll',
          descriptionFormatRules({
            links: {
              'help-link': { href: t('helpUrl'), target: '_blank' },
            },
          }),
        )}
      </FormattedText>
    </div>
  );
}

function HubspotAdminManageDescription({
  canManageTeamHubspot,
}: {
  canManageTeamHubspot: boolean;
}) {
  const t = useTranslations('settings:integrations.apps.hubspot');
  if (!canManageTeamHubspot) {
    return null;
  }
  return (
    <FormattedText>
      {t.rich('descriptionAdminManage', descriptionFormatRules())}
    </FormattedText>
  );
}

function HubspotAdminConnectedAllDescription({
  hubspotAccount,
  isAdmin,
}: {
  hubspotAccount: HubspotSyncedAccount;
  isAdmin: boolean;
}) {
  const t = useTranslations('settings:integrations.apps.hubspot');
  const { canManageTeamHubspot } = useHubspotSettings();

  // Show this description when:
  // 1. User is admin
  // 2. Account is not a member link (team-wide connection)
  // 3. But cannot manage team hubspot (no autofill config available)
  if (!isAdmin || hubspotAccount?.is_member_link || canManageTeamHubspot) {
    return null;
  }

  return (
    <FormattedText>
      {t.rich(
        'descriptionIsAdminAllAccountedConnected',
        descriptionFormatRules(),
      )}
    </FormattedText>
  );
}

function HubspotIndividualUserConnectedDescription({
  hubspotAccount,
  isAdmin,
}: {
  hubspotAccount: HubspotSyncedAccount;
  isAdmin: boolean;
}) {
  const t = useTranslations('settings:integrations.apps.hubspot');
  if (hubspotAccount?.is_member_link || isAdmin) {
    return null;
  }

  return (
    <FormattedText>
      {t.rich('descriptionNotAdminNotConnected', descriptionFormatRules())}
    </FormattedText>
  );
}
